import React, { useEffect, useState } from "react";
import {
  Box,
  useTheme,
  CircularProgress,
  Typography,
  Paper,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  GroupOutlined,
  PersonAddOutlined,
  Inventory2Outlined,
  CheckroomOutlined,
  ShoppingBagOutlined,
  ReceiptLongOutlined,
  RefreshOutlined,
  ArrowUpwardRounded,
} from "@mui/icons-material";
import ApiConfig from "../../Config";
import dashBoardService from "../../repository/dashBoardReppsitory";

const Dashboard = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const colors = {
    background: isDark ? "#11100F" : "#F6F3EF",
    card: isDark ? "#1A1816" : "#FFFFFF",
    cardHover: isDark ? "#211F1C" : "#FCFBF9",
    text: isDark ? "#F4F0EA" : "#27231F",
    secondary: isDark ? "#A9A39B" : "#77716A",
    accent: isDark ? "#D0B89A" : "#806B52",
    accentSoft: isDark ? "rgba(208,184,154,0.11)" : "rgba(128,107,82,0.09)",
    border: isDark ? "rgba(255,255,255,0.07)" : "rgba(50,40,30,0.08)",
  };

  const fetchDashboard = async (showRefresh = false) => {
    try {
      if (showRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const response = await dashBoardService.dashboardAPI();
      if (!response.success) {
        throw new Error(`Dashboard API failed: ${response}`);
      }

      const data = response?.data || response;

      setDashboardData({
        users: data?.users || {},
        products: data?.products || {},
        orders: data?.orders || {},
      });
    } catch (error) {
      console.error("Error fetching dashboard:", error);
      setDashboardData(null);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  const StatCard = ({ title, value, subtitle, icon, highlight = false }) => (
    <Paper
      elevation={0}
      sx={{
        position: "relative",
        minHeight: 175,
        p: 3,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        borderRadius: "10px",
        backgroundColor: highlight ? colors.accent : colors.card,
        border: `1px solid ${highlight ? colors.accent : colors.border}`,
        color: highlight ? "#fff" : colors.text,
        transition:
          "transform .25s ease,box-shadow .25s ease,background .25s ease",
        "&:hover": {
          transform: "translateY(-4px)",
          backgroundColor: highlight ? colors.accent : colors.cardHover,
          boxShadow: isDark
            ? "0 14px 40px rgba(0,0,0,0.25)"
            : "0 14px 40px rgba(60,45,30,0.08)",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
        }}
      >
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: "9px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: highlight
              ? "rgba(255,255,255,0.15)"
              : colors.accentSoft,
            color: highlight ? "#fff" : colors.accent,
          }}
        >
          {React.cloneElement(icon, {
            sx: {
              fontSize: 21,
              color: "inherit",
            },
          })}
        </Box>

        {!highlight && (
          <ArrowUpwardRounded
            sx={{
              fontSize: 17,
              color: colors.accent,
              opacity: 0.65,
            }}
          />
        )}
      </Box>

      <Box>
        <Typography
          sx={{
            fontSize: 34,
            lineHeight: 1,
            fontWeight: 600,
            letterSpacing: "-1px",
            color: "inherit",
            mb: 1,
          }}
        >
          {value ?? 0}
        </Typography>

        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 600,
            color: "inherit",
            opacity: highlight ? 1 : 0.9,
          }}
        >
          {title}
        </Typography>

        {subtitle && (
          <Typography
            sx={{
              mt: 0.5,
              fontSize: 11,
              color: highlight ? "rgba(255,255,255,0.68)" : colors.secondary,
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
    </Paper>
  );

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100%",
          backgroundColor: colors.background,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 4,
        }}
      >
        <Box sx={{ textAlign: "center" }}>
          <CircularProgress
            size={28}
            thickness={2}
            sx={{ color: colors.accent }}
          />

          <Typography
            sx={{
              mt: 2,
              fontSize: 12,
              color: colors.secondary,
              letterSpacing: ".5px",
            }}
          >
            Preparing your dashboard...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (!dashboardData) {
    return (
      <Box
        sx={{
          minHeight: "100%",
          backgroundColor: colors.background,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 4,
        }}
      >
        <Box sx={{ textAlign: "center" }}>
          <Typography
            sx={{
              fontFamily: "Georgia,serif",
              fontSize: 24,
              color: colors.text,
              mb: 1,
            }}
          >
            Dashboard unavailable
          </Typography>

          <Typography
            sx={{
              fontSize: 13,
              color: colors.secondary,
              mb: 3,
            }}
          >
            We couldn't load your store information.
          </Typography>

          <Chip
            label="Try again"
            onClick={() => fetchDashboard()}
            sx={{
              backgroundColor: colors.accent,
              color: "#fff",
              borderRadius: "5px",
              cursor: "pointer",
              "&:hover": {
                backgroundColor: colors.accent,
                opacity: 0.9,
              },
            }}
          />
        </Box>
      </Box>
    );
  }

  const users = dashboardData.users || {};
  const products = dashboardData.products || {};
  const orders = dashboardData.orders || {};

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100%",
        backgroundColor: colors.background,
        px: {
          xs: 2,
          sm: 3,
          md: 4,
        },
        py: {
          xs: 2,
          md: 3,
        },
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: 11,
              textTransform: "uppercase",
              letterSpacing: "2.5px",
              fontWeight: 700,
              color: colors.accent,
              mb: 0.5,
            }}
          >
            Hindustan Wardrobe
          </Typography>

          <Typography
            sx={{
              fontFamily: "Georgia,'Times New Roman',serif",
              fontSize: {
                xs: 28,
                md: 34,
              },
              fontWeight: 400,
              letterSpacing: "-1px",
              color: colors.text,
              lineHeight: 1.1,
            }}
          >
            Dashboard
          </Typography>

          <Typography
            sx={{
              mt: 0.7,
              fontSize: 13,
              color: colors.secondary,
            }}
          >
            A clear view of your wardrobe business.
          </Typography>
        </Box>

        <Tooltip title="Refresh dashboard">
          <IconButton
            onClick={() => fetchDashboard(true)}
            disabled={refreshing}
            sx={{
              width: 42,
              height: 42,
              flexShrink: 0,
              border: `1px solid ${colors.border}`,
              borderRadius: "8px",
              color: colors.secondary,
              "&:hover": {
                backgroundColor: colors.card,
                color: colors.accent,
              },
            }}
          >
            {refreshing ? (
              <CircularProgress
                size={18}
                thickness={2}
                sx={{ color: colors.accent }}
              />
            ) : (
              <RefreshOutlined fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography
          sx={{
            fontSize: 11,
            letterSpacing: "2px",
            textTransform: "uppercase",
            fontWeight: 700,
            color: colors.accent,
          }}
        >
          Overview
        </Typography>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2,1fr)",
            lg: "repeat(4,1fr)",
          },
          gap: 2,
          mb: 4,
        }}
      >
        <StatCard
          title="Total Customers"
          value={users.totalUsers}
          subtitle="Registered users"
          icon={<GroupOutlined />}
          highlight
        />

        <StatCard
          title="New Customers"
          value={users.newUsersLastFiveDays}
          subtitle="Last 5 days"
          icon={<PersonAddOutlined />}
        />

        <StatCard
          title="Total Products"
          value={products.totalProducts}
          subtitle="Across your catalog"
          icon={<Inventory2Outlined />}
        />

        <StatCard
          title="Active Products"
          value={products.activeProducts}
          subtitle="Currently available"
          icon={<ShoppingBagOutlined />}
        />
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography
          sx={{
            fontSize: 11,
            letterSpacing: "2px",
            textTransform: "uppercase",
            fontWeight: 700,
            color: colors.accent,
          }}
        >
          Store Operations
        </Typography>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2,1fr)",
            md: "repeat(3,1fr)",
          },
          gap: 2,
        }}
      >
        <StatCard
          title="Total Orders"
          value={orders.totalOrder}
          subtitle="All orders"
          icon={<ReceiptLongOutlined />}
          highlight
        />

        <StatCard
          title="Bookings"
          value={orders.totalBooking}
          subtitle="Wardrobe bookings"
          icon={<ShoppingBagOutlined />}
        />

        <StatCard
          title="Try-On Sessions"
          value={orders.totalTryOn}
          subtitle="Customer try-ons"
          icon={<CheckroomOutlined />}
        />
      </Box>

      <Box
        sx={{
          mt: 5,
          pt: 3,
          borderTop: `1px solid ${colors.border}`,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexDirection: {
            xs: "column",
            sm: "row",
          },
          gap: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: 11,
            color: colors.secondary,
          }}
        >
          Hindustan Wardrobe Admin
        </Typography>

        <Typography
          sx={{
            fontSize: 11,
            color: colors.secondary,
          }}
        >
          Store intelligence & operations
        </Typography>
      </Box>
    </Box>
  );
};

export default Dashboard;
